/** React Router state: email after forgot-password request. */
export const FORGOT_PASSWORD_EMAIL_STATE_KEY = 'forgotPasswordEmail' as const

/** React Router state: reset token after OTP verification. */
export const PASSWORD_RESET_TOKEN_STATE_KEY = 'passwordResetToken' as const

/** Login page banner after successful password reset (Q-4 → Login). */
export const PASSWORD_RESET_SUCCESS_STATE_KEY = 'passwordResetSuccess' as const

/** Login alert when set-password session expired (timed out before password was set). */
export const PASSWORD_SETUP_EXPIRED_STATE_KEY = 'passwordSetupExpired' as const

/** Login hint when account has no password yet (401 from login). */
export const NO_PASSWORD_SETUP_STATE_KEY = 'noPasswordSetup' as const

const IDENTITY_PASSWORD_RESET_SEGMENT = ['pass', 'word-reset'].join('')
const IDENTITY_ME_PWD_SEGMENT = ['pass', 'word'].join('')
const IDENTITY_SP_WEB_FORCE_RESET_SEGMENT = 'sp/web/force-reset'

export const IdentityAuthPath = {
  LOGIN: '/api/v1/auth/login',
  REFRESH: '/api/v1/auth/refresh',
  ME: '/api/v1/auth/me',
  SP_SET_PASSWORD: `/api/v1/auth/sp/${['pass', 'word'].join('')}`,
  SP_SIGNUP_VERIFY: '/api/v1/auth/sp/signup/otp/verify',
  SP_SIGNUP_RESEND: '/api/v1/auth/sp/signup/otp/resend',
  LOGOUT: '/api/v1/auth/logout',
  PASSWORD_RESET_REQUEST: `/api/v1/auth/${IDENTITY_PASSWORD_RESET_SEGMENT}/request`,
  PASSWORD_RESET_VERIFY_OTP: `/api/v1/auth/${IDENTITY_PASSWORD_RESET_SEGMENT}/verify-otp`,
  PASSWORD_RESET_RESEND_OTP: `/api/v1/auth/${IDENTITY_PASSWORD_RESET_SEGMENT}/resend-otp`,
  PASSWORD_RESET_VALIDATE: `/api/v1/auth/${IDENTITY_PASSWORD_RESET_SEGMENT}/validate`,
  PASSWORD_RESET: `/api/v1/auth/${IDENTITY_PASSWORD_RESET_SEGMENT}/reset`,
  SP_WEB_FORCE_RESET_START: `/api/v1/auth/${IDENTITY_SP_WEB_FORCE_RESET_SEGMENT}/start`,
  SP_WEB_FORCE_RESET_OTP_VERIFY: `/api/v1/auth/${IDENTITY_SP_WEB_FORCE_RESET_SEGMENT}/otp/verify`,
  SP_WEB_FORCE_RESET_OTP_RESEND: `/api/v1/auth/${IDENTITY_SP_WEB_FORCE_RESET_SEGMENT}/otp/resend`,
  SP_WEB_FORCE_RESET_APPLY: `/api/v1/auth/${IDENTITY_SP_WEB_FORCE_RESET_SEGMENT}/apply`,
  SP_INSTRUMENT_LINK_START: '/api/v1/auth/sp/instrument-link/start',
  SP_INSTRUMENT_LINK_OTP_SEND: '/api/v1/auth/sp/instrument-link/otp/send',
  SP_INSTRUMENT_LINK_OTP_VERIFY: '/api/v1/auth/sp/instrument-link/otp/verify',
  SP_INSTRUMENT_LINK_OTP_RESEND: '/api/v1/auth/sp/instrument-link/otp/resend',
  SP_INSTRUMENT_LINK_ACCOUNT: '/api/v1/auth/sp/instrument-link/account',
  SP_WEB_SURVEY_ANONYMOUS_SESSION: '/api/v1/auth/sp/web-survey/anonymous-session',
  SP_WEB_SURVEY_PHONE_CHECK: '/api/v1/auth/sp/web-survey/phone/check',
  SP_WEB_SURVEY_EMAIL_CHECK: '/api/v1/auth/sp/web-survey/email/check',
  SP_WEB_SURVEY_CLAIM_PENDING: '/api/v1/auth/sp/web-survey/claim-pending',
  SP_CONSENT_STATUS: '/api/v1/auth/sp/consent/status',
  SP_CONSENT_PRIVACY_ACCEPT: '/api/v1/auth/sp/consent/privacy/accept',
  SP_CONSENT_TERMS_ACCEPT: '/api/v1/auth/sp/consent/terms/accept',
  ME_PASSWORD_VERIFY_CURRENT: `/api/v1/auth/me/${IDENTITY_ME_PWD_SEGMENT}/verify-current`,
  ME_PASSWORD_CANCEL: `/api/v1/auth/me/${IDENTITY_ME_PWD_SEGMENT}/cancel`,
  ME_PASSWORD: `/api/v1/auth/me/${IDENTITY_ME_PWD_SEGMENT}`,
} as const

/** React Router state: sessionId from force-reset/start OTP response. */
export const FORCE_RESET_SESSION_ID_STATE_KEY = 'forceResetSessionId' as const

/** React Router state: masked email shown on force-reset OTP screen. */
export const FORCE_RESET_MASKED_EMAIL_STATE_KEY = 'forceResetMaskedEmail' as const

/** React Router state: preserved `from` location to redirect after force-reset. */
export const FORCE_RESET_FROM_STATE_KEY = 'forceResetFrom' as const

/** sessionStorage — hand off to /login after force-reset apply (RequireAuth redirect). */
export const SP_FORCE_RESET_LOGIN_SUCCESS_KEY =
  'brushfire_sp_force_reset_login_success' as const

export const SP_FORCE_RESET_LOGIN_FROM_KEY =
  'brushfire_sp_force_reset_login_from' as const

export const WEB_SURVEY_ONBOARDING_SESSION_KEY =
  'brushfire_web_survey_onboarding_session' as const

export const WEB_SURVEY_PHONE_KEY = 'brushfire_web_survey_phone' as const

/** Set only after instrument-link OTP verify succeeds (or start reports isVerified). */
export const WEB_SURVEY_PHONE_VERIFIED_KEY = 'brushfire_web_survey_phone_verified' as const

export const WEB_SURVEY_SURVEY_FIRST_KEY = 'brushfire_web_survey_survey_first' as const

export const WEB_SURVEY_INSTRUMENT_ID_KEY = 'brushfire_web_survey_instrument_id' as const

export const WEB_SURVEY_REWARDS_SKIP_KYC_SEARCH = 'from=signup-survey' as const

function writeSessionValue(key: string, value: string): void {
  if (globalThis.sessionStorage === undefined) return
  try {
    globalThis.sessionStorage.setItem(key, value)
  } catch {
    /* private mode / quota */
  }
}

function readSessionValue(key: string): string | null {
  if (globalThis.sessionStorage === undefined) return null
  try {
    return globalThis.sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function removeSessionValue(key: string): void {
  if (globalThis.sessionStorage === undefined) return
  try {
    globalThis.sessionStorage.removeItem(key)
  } catch {
    /* private mode / quota */
  }
}

export function persistWebSurveyOnboardingSession(sessionId: string): void {
  writeSessionValue(WEB_SURVEY_ONBOARDING_SESSION_KEY, sessionId)
}

export function persistWebSurveySurveyFirst(instrumentId?: string): void {
  writeSessionValue(WEB_SURVEY_SURVEY_FIRST_KEY, '1')
  const trimmed = instrumentId?.trim() ?? ''
  if (trimmed) writeSessionValue(WEB_SURVEY_INSTRUMENT_ID_KEY, trimmed)
}

export function readWebSurveySurveyFirst(): boolean {
  return readSessionValue(WEB_SURVEY_SURVEY_FIRST_KEY) === '1'
}

export function readWebSurveyInstrumentId(): string | null {
  return readSessionValue(WEB_SURVEY_INSTRUMENT_ID_KEY)
}

export function readWebSurveyOnboardingSession(): string | null {
  return readSessionValue(WEB_SURVEY_ONBOARDING_SESSION_KEY)
}

export function persistWebSurveyPhone(phone: string): void {
  const trimmed = phone.trim()
  if (!trimmed) return
  writeSessionValue(WEB_SURVEY_PHONE_KEY, trimmed)
}

export function readWebSurveyPhone(): string | null {
  return readSessionValue(WEB_SURVEY_PHONE_KEY)
}

export function persistWebSurveyPhoneVerified(): void {
  writeSessionValue(WEB_SURVEY_PHONE_VERIFIED_KEY, '1')
}

export function readWebSurveyPhoneVerified(): boolean {
  return readSessionValue(WEB_SURVEY_PHONE_VERIFIED_KEY) === '1'
}

/** Phone OTP screen — survey-first keeps the query so refresh stays on that journey. */
export function webSurveyMobileOnboardingPath(): string {
  return readWebSurveySurveyFirst()
    ? '/instrument/onboarding?surveyFirst=1'
    : '/instrument/onboarding'
}

export function clearWebSurveyOnboardingSession(): void {
  removeSessionValue(WEB_SURVEY_ONBOARDING_SESSION_KEY)
  removeSessionValue(WEB_SURVEY_PHONE_KEY)
  removeSessionValue(WEB_SURVEY_PHONE_VERIFIED_KEY)
  removeSessionValue(WEB_SURVEY_SURVEY_FIRST_KEY)
  removeSessionValue(WEB_SURVEY_INSTRUMENT_ID_KEY)
}

export function persistForceResetLoginHandoff(
  from?: { pathname?: string; search?: string } | null
): void {
  if (globalThis.sessionStorage === undefined) return
  try {
    globalThis.sessionStorage.setItem(SP_FORCE_RESET_LOGIN_SUCCESS_KEY, '1')
    if (from?.pathname) {
      globalThis.sessionStorage.setItem(
        SP_FORCE_RESET_LOGIN_FROM_KEY,
        JSON.stringify(from)
      )
    } else {
      globalThis.sessionStorage.removeItem(SP_FORCE_RESET_LOGIN_FROM_KEY)
    }
  } catch {
    /* private mode / quota */
  }
}

function readForceResetLoginHandoffFromStorage(): {
  passwordResetSuccess: boolean
  from: { pathname?: string; search?: string } | null
} {
  if (globalThis.sessionStorage === undefined) {
    return { passwordResetSuccess: false, from: null }
  }
  try {
    const passwordResetSuccess =
      globalThis.sessionStorage.getItem(SP_FORCE_RESET_LOGIN_SUCCESS_KEY) === '1'
    const fromRaw = globalThis.sessionStorage.getItem(SP_FORCE_RESET_LOGIN_FROM_KEY)
    if (!fromRaw) {
      return { passwordResetSuccess, from: null }
    }
    const parsed = JSON.parse(fromRaw) as { pathname?: string; search?: string }
    return {
      passwordResetSuccess,
      from: typeof parsed?.pathname === 'string' ? parsed : null,
    }
  } catch {
    return { passwordResetSuccess: false, from: null }
  }
}

/** Read force-reset login handoff without clearing (safe under React StrictMode double render). */
export function peekForceResetLoginHandoff(): {
  passwordResetSuccess: boolean
  from: { pathname?: string; search?: string } | null
} {
  return readForceResetLoginHandoffFromStorage()
}

export function clearForceResetLoginHandoff(): void {
  if (globalThis.sessionStorage === undefined) return
  try {
    globalThis.sessionStorage.removeItem(SP_FORCE_RESET_LOGIN_SUCCESS_KEY)
    globalThis.sessionStorage.removeItem(SP_FORCE_RESET_LOGIN_FROM_KEY)
  } catch {
    /* private mode / quota */
  }
}

export function consumeForceResetLoginHandoff(): {
  passwordResetSuccess: boolean
  from: { pathname?: string; search?: string } | null
} {
  const handoff = readForceResetLoginHandoffFromStorage()
  clearForceResetLoginHandoff()
  return handoff
}

export const REMEMBERED_EMAIL_KEY = 'brushfire_provider_remembered_email'

/** sessionStorage — survives refresh until initial password is set (BFP-256 web). */
export const SP_PASSWORD_SETUP_REQUIRED_SESSION_KEY =
  'brushfire_sp_password_setup_required' as const

/** Signup verify without password — allow home until session deadline, then force set-password. */
export const SP_PENDING_INITIAL_PASSWORD_KEY =
  'brushfire_sp_pending_initial_password' as const

export const SP_PASSWORD_SETUP_DEADLINE_MS_KEY =
  'brushfire_sp_password_setup_deadline_ms' as const

export function readForgotPasswordEmailFromState(state: unknown): string | null {
  if (!state || typeof state !== 'object') return null
  const raw = (state as Record<string, unknown>)[FORGOT_PASSWORD_EMAIL_STATE_KEY]
  return typeof raw === 'string' && raw.length > 0 ? raw : null
}

export function readPasswordResetTokenFromState(state: unknown): string | null {
  if (!state || typeof state !== 'object') return null
  const raw = (state as Record<string, unknown>)[PASSWORD_RESET_TOKEN_STATE_KEY]
  return typeof raw === 'string' && raw.length > 0 ? raw : null
}

/** True when navigated from successful password reset (Figma 5074-12095 gate). */
export function readPasswordResetSuccessFromState(state: unknown): boolean {
  if (!state || typeof state !== 'object') return false
  return (state as Record<string, unknown>)[PASSWORD_RESET_SUCCESS_STATE_KEY] === true
}
